import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  IDtoListingBlackoutTimes,
  IDtoListingBooking,
  IDtoListingLocation,
  IDtoSiteName,
  IListing,
  IListingExtra,
  IListingWhere,
  IPhoto,
  IPolicy,
  IPrimaryPropertyType,
  IPropertyDescriptor,
  ITax,
} from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BASE_API_URL } from '../network/api-config';

const API_URL = BASE_API_URL + 'listing';

/**
 * Local fork of @curbnturf/listing's ListingService — trimmed to plain
 * HttpClient and to the methods actually called. createPolicy/
 * createSignalReception/findListingForSite/restore/removePolicy/
 * removeSignalReception/upsert/generic update/updatePropertyType/
 * updateAmenities/updatePhotos/updateDiscount/enableAndHide/hide/show/
 * retrieveLocalOwnedListings (native SQLite) aren't used.
 */
@Injectable({ providedIn: 'root' })
export class ListingApiService {
  private readonly http = inject(HttpClient);

  create(listing: IListing): Observable<IListing> {
    return this.http.post<IListing>(API_URL, this.formatForServer(listing));
  }

  fetch(listingId: number): Observable<IListing> {
    return this.http.get<IListing>(`${API_URL}/${listingId}`);
  }

  fetchAll(
    query: IListingWhere,
    sort = 'id',
    page = 1,
    pageSize = 100,
  ): Observable<{ total: number; page: number; listings: IListing[] }> {
    const queryString = objectToUrlParams({ ...query, sort, page, pageSize });
    return this.http.get<{ total: number; page: number; listings: IListing[] }>(`${API_URL}?${queryString}`);
  }

  remove(listingId: number): Observable<IListing> {
    return this.http.delete<IListing>(`${API_URL}/${listingId}`);
  }

  /** A fresh random set of up to 10 admin-curated featured listings, for the marketing home page. */
  getFeaturedListings(): Observable<IListing[]> {
    return this.http.get<IListing[]>(`${API_URL}/featured`);
  }

  updateLocation(listingId: number, listingDto: IDtoListingLocation): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/location`, listingDto);
  }

  updateBooking(listingId: number, listingDto: IDtoListingBooking): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/booking`, listingDto);
  }

  /**
   * Direct-set variant of updatePropertyType — sends the real primaryType/
   * descriptors as-is instead of deriving them server-side from the
   * deprecated ILandType vocabulary. Same endpoint, new payload shape.
   */
  updatePrimaryTypeAndDescriptors(
    listingId: number,
    primaryType: IPrimaryPropertyType['type'],
    descriptors: IPropertyDescriptor['type'][],
    customPrimaryLabel?: string,
  ): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/property-types`, {
      primaryType,
      descriptors,
      customPrimaryLabel,
    });
  }

  updatePotentialHazards(
    listingId: number,
    potentialHazards: boolean,
    potentialHazardDescription: string,
  ): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/potential-hazards`, {
      potentialHazards,
      potentialHazardDescription,
    });
  }

  updatePolicies(listingId: number, policies: IPolicy[]): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/policies/`, policies);
  }

  updateExtras(listingId: number, extras: IListingExtra[]): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/extras/`, extras);
  }

  updateBlackoutTimes(listingId: number, blackoutTimes: IDtoListingBlackoutTimes): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/blackout-times/`, blackoutTimes);
  }

  updateDescription(listingId: number, data: IDtoSiteName): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/description/`, data);
  }

  /**
   * Updates just a listing's applied taxes. Unlike updateExtras/updatePolicies, there's no
   * dedicated `/listing/:id/taxes` sub-endpoint — the server only syncs ListingTaxEntity rows
   * as a side effect of the generic `PUT /listing/:id` (see listingService.update()'s
   * syncSubElements() -> listingTaxService.sync()). So this fetches the FULL current entity
   * first and PUTs it back whole with only `.taxes` swapped, rather than sending a stripped-down
   * partial payload that could blank out fields (sites, photos, amenities, etc.) the caller
   * doesn't have loaded.
   */
  updateTaxes(listingId: number, taxes: ITax[]): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.fetch(listingId).pipe(
      switchMap((full) => this.http.put<IListing>(`${API_URL}/${listingId}`, { ...full, taxes })),
    );
  }

  addNewSites(listingId: number, numberOfSites: number): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/add-site`, { numberOfSites });
  }

  enable(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/enable`, null);
  }

  disable(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/disable`, null);
  }

  removeImage(listingId: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${listingId}/remove-image/${photoIndex}`);
  }

  uploadImage(listingId: number, formData: FormData): Observable<{ id: number; photos: IPhoto[] }> {
    return this.http.post<{ id: number; photos: IPhoto[] }>(`${API_URL}/${listingId}/upload-image`, formData);
  }

  favorite(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/favorite`, null);
  }

  unfavorite(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/unfavorite`, null);
  }

  private formatForServer(listing: IListing): IListing {
    return { ...listing, sites: undefined };
  }
}
